import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import AdDisplay from '../components/AdDisplay';
import ShareButton, { getOGShareUrl } from '../components/ShareButton';
import NotificationBanner from '../components/NotificationBanner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Star, ChevronRight, Heart, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BettingStrategies = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [allStrategies, setAllStrategies] = useState([]); // Store all strategies
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('random');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [likedItems, setLikedItems] = useState(new Set());
  const [reviewStats, setReviewStats] = useState({});
  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    fetchStrategies();
    if (user) {
      fetchLikes();
    }
  }, [filter, user]);

  useEffect(() => {
    // Sort and paginate when sortBy or currentPage changes
    sortAndPaginateStrategies();
  }, [sortBy, currentPage, allStrategies, reviewStats]);

  // Re-fetch likes and reviews when page becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        fetchLikes();
        fetchReviewStats(); // Refresh review stats when user returns
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, allStrategies]);

  const fetchReviewStats = async () => {
    if (allStrategies.length === 0) return;
    
    try {
      const statsPromises = allStrategies.map(strategy =>
        axios.get(`${API}/reviews/strategy/${strategy.id}`).catch(() => ({ data: { average_rating: 0, total_reviews: 0 } }))
      );
      const statsResults = await Promise.all(statsPromises);
      const stats = {};
      allStrategies.forEach((strategy, idx) => {
        stats[strategy.id] = statsResults[idx].data;
      });
      setReviewStats(stats);
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('category', filter);
      
      const response = await axios.get(`${API}/betting-strategies?${params.toString()}`);
      const strategiesData = response.data;
      setAllStrategies(strategiesData);

      // Fetch review stats for each strategy
      const statsPromises = strategiesData.map(strategy =>
        axios.get(`${API}/reviews/strategy/${strategy.id}`).catch(() => ({ data: { average_rating: 0, total_reviews: 0 } }))
      );
      const statsResults = await Promise.all(statsPromises);
      const stats = {};
      strategiesData.forEach((strategy, idx) => {
        stats[strategy.id] = statsResults[idx].data;
      });
      setReviewStats(stats);
      setCurrentPage(1); // Reset to first page when filter changes
    } catch (error) {
      console.error('Error fetching strategies:', error);
      toast.error('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  const sortAndPaginateStrategies = () => {
    let sorted = [...allStrategies];

    // Sort
    switch (sortBy) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'popularity':
        // Count likes for each strategy
        sorted.sort((a, b) => {
          const aLikes = likedItems.has(a.id) ? 1 : 0;
          const bLikes = likedItems.has(b.id) ? 1 : 0;
          return bLikes - aLikes;
        });
        break;
      case 'best_review':
        sorted.sort((a, b) => {
          const aRating = reviewStats[a.id]?.average_rating || 0;
          const bRating = reviewStats[b.id]?.average_rating || 0;
          return bRating - aRating;
        });
        break;
      case 'random':
      default:
        // Shuffle array for random
        for (let i = sorted.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
        }
        break;
    }

    // Paginate
    const total = Math.ceil(sorted.length / ITEMS_PER_PAGE);
    setTotalPages(total);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    setStrategies(sorted.slice(start, end));
  };

  const fetchLikes = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/likes/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const liked = new Set(response.data.strategies.map(s => s.id));
      setLikedItems(liked);
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const handleToggleLike = async (e, strategyId) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to like strategies');
      navigate('/login');
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(
        `${API}/likes/toggle?item_id=${strategyId}&item_type=strategy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.liked) {
        setLikedItems(prev => new Set([...prev, strategyId]));
      } else {
        setLikedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(strategyId);
          return newSet;
        });
      }
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'System': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'Method': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Strategy': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-400';
      case 'Intermediate': return 'text-yellow-400';
      case 'Advanced': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading strategies...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Notification Banner */}
          <NotificationBanner type="strategies" />

          <div className="flex items-center mb-8">
            <BookOpen className="w-12 h-12 text-blue-400 mr-4" />
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Betting Strategies, Systems & Methods</h1>
              <p className="text-gray-400 mt-2">Learn proven strategies to improve your betting performance</p>
            </div>
          </div>

          {/* Category Filter and Sort */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
            <div className="flex gap-3 overflow-x-auto">
              <Button
                onClick={() => setFilter('all')}
                variant={filter === 'all' ? 'default' : 'outline'}
                className={filter === 'all' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
              >
                All
              </Button>
              <Button
                onClick={() => setFilter('Strategy')}
                variant={filter === 'Strategy' ? 'default' : 'outline'}
                className={filter === 'Strategy' ? 'bg-purple-600 hover:bg-purple-700' : 'border-white/20 text-white hover:bg-white/10'}
              >
                Strategies
              </Button>
              <Button
                onClick={() => setFilter('System')}
                variant={filter === 'System' ? 'default' : 'outline'}
                className={filter === 'System' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
              >
                Systems
              </Button>
              <Button
                onClick={() => setFilter('Method')}
                variant={filter === 'Method' ? 'default' : 'outline'}
                className={filter === 'Method' ? 'bg-green-600 hover:bg-green-700' : 'border-white/20 text-white hover:bg-white/10'}
              >
                Methods
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400 whitespace-nowrap">Sort by:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Random</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="popularity">Popularity</SelectItem>
                  <SelectItem value="best_review">Best Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {strategies.length === 0 ? (
            <Card className="glass-dark border-white/10">
              <CardContent className="pt-6">
                <div className="text-center py-16">
                  <BookOpen className="w-24 h-24 text-blue-400 mx-auto mb-6 opacity-50" />
                  <h2 className="text-2xl font-bold text-white mb-4">No Strategies Yet</h2>
                  <p className="text-gray-400 text-lg">
                    Betting strategies will be available here soon.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Strategies 1-3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {strategies.slice(0, 3).map((strategy) => {
                  const stats = reviewStats[strategy.id] || { average_rating: 0, total_reviews: 0 };
                  const isLiked = likedItems.has(strategy.id);
                  
                  return (
                    <Card key={strategy.id} className="glass-dark border-white/10 hover:border-blue-500/50 transition-all group">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(strategy.category)}`}>
                              {strategy.category}
                            </span>
                            {strategy.is_featured && (
                              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Rating Display - Left of Heart */}
                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= Math.round(stats.average_rating)
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-gray-600'
                                  }`}
                                />
                              ))}
                              <span className="text-sm font-semibold text-white ml-1">
                                {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '0.0'}
                              </span>
                            </div>
                            <Button
                              onClick={(e) => handleToggleLike(e, strategy.id)}
                              variant="ghost"
                              size="sm"
                              className={`${isLiked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 p-1`}
                            >
                              <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-400' : ''}`} />
                            </Button>
                          </div>
                        </div>

                        <CardTitle 
                          className="text-white group-hover:text-blue-400 transition-colors cursor-pointer"
                          onClick={() => navigate(`/betting-strategies/${strategy.id}`)}
                        >
                          {strategy.title}
                        </CardTitle>
                        <p className={`text-sm font-medium ${getDifficultyColor(strategy.difficulty)}`}>
                          {strategy.difficulty}
                        </p>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                          {strategy.description}
                        </p>
                        {strategy.tags && strategy.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {strategy.tags.slice(0, 3).map((tag, idx) => (
                              <span key={idx} className="text-xs px-2 py-1 rounded glass text-gray-300">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => navigate(`/betting-strategies/${strategy.id}`)}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 group-hover:bg-blue-500"
                          >
                            Read More
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                          <ShareButton 
                            shareUrl={getOGShareUrl('betting-strategies', strategy.id)}
                            title={strategy.title}
                            description={`Check out this betting strategy: ${strategy.title} on BetBanky`}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Ad Row 1 - Show if 3+ strategies exist */}
              {strategies.length >= 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <AdDisplay page="strategies" position="row1_ad1" />
                  <AdDisplay page="strategies" position="row1_ad2" />
                </div>
              )}

              {/* Strategies 4-9 */}
              {strategies.length > 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {strategies.slice(3, 9).map((strategy) => {
                    const stats = reviewStats[strategy.id] || { average_rating: 0, total_reviews: 0 };
                    const isLiked = likedItems.has(strategy.id);
                    
                    return (
                      <Card key={strategy.id} className="glass-dark border-white/10 hover:border-blue-500/50 transition-all group">
                        <CardHeader>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(strategy.category)}`}>
                                {strategy.category}
                              </span>
                              {strategy.is_featured && (
                                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Rating Display - Left of Heart */}
                              <div className="flex items-center space-x-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`w-4 h-4 ${
                                      star <= Math.round(stats.average_rating)
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-600'
                                    }`}
                                  />
                                ))}
                                <span className="text-sm font-semibold text-white ml-1">
                                  {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '0.0'}
                                </span>
                              </div>
                              <Button
                                onClick={(e) => handleToggleLike(e, strategy.id)}
                                variant="ghost"
                                size="sm"
                                className={`${isLiked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 p-1`}
                              >
                                <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-400' : ''}`} />
                              </Button>
                            </div>
                          </div>

                          <CardTitle 
                            className="text-white group-hover:text-blue-400 transition-colors cursor-pointer"
                            onClick={() => navigate(`/betting-strategies/${strategy.id}`)}
                          >
                            {strategy.title}
                          </CardTitle>
                          <p className={`text-sm font-medium ${getDifficultyColor(strategy.difficulty)}`}>
                            {strategy.difficulty}
                          </p>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                            {strategy.description}
                          </p>
                          {strategy.tags && strategy.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {strategy.tags.slice(0, 3).map((tag, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 rounded glass text-gray-300">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => navigate(`/betting-strategies/${strategy.id}`)}
                              className="flex-1 bg-blue-600 hover:bg-blue-700 group-hover:bg-blue-500"
                            >
                              Read More
                              <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                            <ShareButton 
                              shareUrl={getOGShareUrl('betting-strategies', strategy.id)}
                              title={strategy.title}
                              description={`Check out this betting strategy: ${strategy.title} on BetBanky`}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Ad Row 2 - Show if 9+ strategies exist (after 9th item) OR at bottom if fewer items */}
              {strategies.length >= 9 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <AdDisplay page="strategies" position="row2_ad1" />
                  <AdDisplay page="strategies" position="row2_ad2" />
                </div>
              )}

              {/* Remaining Strategies (10+) */}
              {strategies.length > 9 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {strategies.slice(9).map((strategy) => {
                    const stats = reviewStats[strategy.id] || { average_rating: 0, total_reviews: 0 };
                    const isLiked = likedItems.has(strategy.id);
                    
                    return (
                      <Card key={strategy.id} className="glass-dark border-white/10 hover:border-blue-500/50 transition-all group">
                        <CardHeader>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(strategy.category)}`}>
                                {strategy.category}
                              </span>
                              {strategy.is_featured && (
                                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Rating Display - Left of Heart */}
                              <div className="flex items-center space-x-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`w-4 h-4 ${
                                      star <= Math.round(stats.average_rating)
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-600'
                                    }`}
                                  />
                                ))}
                                <span className="text-sm font-semibold text-white ml-1">
                                  {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '0.0'}
                                </span>
                              </div>
                              <Button
                                onClick={(e) => handleToggleLike(e, strategy.id)}
                                variant="ghost"
                                size="sm"
                                className={`${isLiked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 p-1`}
                              >
                                <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-400' : ''}`} />
                              </Button>
                            </div>
                          </div>

                          <CardTitle 
                            className="text-white group-hover:text-blue-400 transition-colors cursor-pointer"
                            onClick={() => navigate(`/betting-strategies/${strategy.id}`)}
                          >
                            {strategy.title}
                          </CardTitle>
                          <p className={`text-sm font-medium ${getDifficultyColor(strategy.difficulty)}`}>
                            {strategy.difficulty}
                          </p>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                            {strategy.description}
                          </p>
                          {strategy.tags && strategy.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {strategy.tags.slice(0, 3).map((tag, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 rounded glass text-gray-300">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => navigate(`/betting-strategies/${strategy.id}`)}
                              className="flex-1 bg-blue-600 hover:bg-blue-700 group-hover:bg-blue-500"
                            >
                              Read More
                              <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                            <ShareButton 
                              shareUrl={getOGShareUrl('betting-strategies', strategy.id)}
                              title={strategy.title}
                              description={`Check out this betting strategy: ${strategy.title} on BetBanky`}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Ad Row 2 at bottom - Show if fewer than 9 strategies but more than 0 */}
              {strategies.length > 0 && strategies.length < 9 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <AdDisplay page="strategies" position="row2_ad1" />
                  <AdDisplay page="strategies" position="row2_ad2" />
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
              <Button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              {/* Page Numbers */}
              {(() => {
                const pages = [];
                const start = Math.max(1, currentPage - 2);
                const end = Math.min(totalPages, currentPage + 2);

                for (let i = start; i <= end; i++) {
                  pages.push(
                    <Button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      variant={currentPage === i ? 'default' : 'outline'}
                      size="sm"
                      className={currentPage === i 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'border-white/20 text-white hover:bg-white/10'}
                    >
                      {i}
                    </Button>
                  );
                }
                return pages;
              })()}

              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BettingStrategies;
